import React, { useEffect, useMemo, useState } from 'react';
import { isBlank } from 'underscore.string';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { removeTypename } from 'shared/helpers/utils';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';

import { MainContent } from 'components/skip-to-main';
import { useModals, ModalNames } from 'components/modals';
import useUser from 'hooks/use-user';
import { getPersistedValue } from 'shared/utils/persisted-values';
import type { ImageBanner as ImageBannerData } from 'src/gql/graphql';

import useUI from 'hooks/use-ui';
import useCurrentDispensary from 'src/dispensary/hooks/use-current-dispensary';
import { getHtmlPlainText } from 'shared/utils/misc-utils';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import useDispensaryMenuSections from 'src/dispensary/hooks/use-dispensary-menu-sections';
import { useHideCategoryTiles } from 'shared/hooks/use-hide-category-tiles';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import useCart from 'shared/hooks/use-cart';
import { useRemoteComponent, RemoteComponentSlot } from 'hooks/use-remote-component';

import { MenuImageBanner } from 'src/components/menu-image-banner';
import { useCashBackHomeBannerSlide } from 'src/payments/components/cash-back-home-banner';
import { usePointsBoostHomeBannerSlide } from 'src/payments/components/points-boost-home-banner';
import { useCashBackSurfacesConfig } from 'src/payments/hooks/use-cash-back-surfaces-config';
import { usePointsBoostSurfacesConfig } from 'src/payments/hooks/use-points-boost-surfaces-config';
import { PersonalizedProducts } from 'components/carousels/products-carousels/personalized-products-carousel';
import { BuyItAgainCarousel } from 'components/carousels/products-carousels/buy-it-again-carousel';
import { HomePageSponsoredBrandCarousel } from 'src/components/carousels/ads-carousels/home-page-sponsored-brand-carousel';
import { CarouselTitleSlotProvider } from 'src/components/carousel-v2/carousel-title-slot-context';
import { useHomeSponsoredBrandPlacement } from 'src/utils/ads/hooks/use-home-sponsored-brand-placement';
import { DutchieLoyaltyWidget } from 'src/components/widgets/dutchie-loyalty-widget';
import { useLoyalty } from 'src/components/widgets/loyalty-tiers/use-loyalty';
import { LoginCta } from 'src/components/ctas';

import { useProductsForHomePage } from 'src/dispensary/hooks/use-products-for-home-page';
import { useShowBuyItAgainCarousel } from 'src/dispensary/hooks/use-show-buy-it-again-carousel';
import { HomepageCarouselSections } from 'components/homepage-carousel-sections';

import MenuBanner from './components/menu-banner';
import { HomeCategoryTiles } from './home-category-tiles';
import { HomePageLoadingState, needsHomePageLoadingState } from './components/home-page-loading-state';

type HomeImageBannerCarouselProps = {
  banners: ImageBannerData[];
};

const HomeImageBannerCarousel = ({ banners }: HomeImageBannerCarouselProps): JSX.Element => {
  const cashBackHomeBannerSlide = useCashBackHomeBannerSlide();
  const pointsBoostHomeBannerSlide = usePointsBoostHomeBannerSlide();

  // At most one dynamic incentive slide: cash when eligible, otherwise points, never both.
  const selectedSlide = cashBackHomeBannerSlide ?? pointsBoostHomeBannerSlide;

  return <MenuImageBanner banners={banners} trailingSlides={selectedSlide ? [selectedSlide] : []} />;
};

export default function CoreMenuDispensaryPage(): JSX.Element | null {
  const UI = useUI();
  const { dispensary } = useCurrentDispensary();
  const { categories } = useDispensaryCategories();
  const hideCategoryTiles = useHideCategoryTiles(dispensary);
  const amplitude = useAmplitude();
  const Cart = useCart();
  const flags = useFlags();
  const dispensaryId = dispensary?.id ?? '';
  const { openModal } = useModals();
  const user = useUser();
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const { homeBanner: isCashBackBannerEnabled } = useCashBackSurfacesConfig();
  const { homeBanner: isPointsBoostBannerEnabled } = usePointsBoostSurfacesConfig();
  const showHomeIncentiveCarousel = isCashBackBannerEnabled || isPointsBoostBannerEnabled;

  const { redemptionType, hasTiers } = useLoyalty();
  const showBuyItAgainCarousel = useShowBuyItAgainCarousel();
  const isKioskProEnabled = useKioskPro();
  const isLoyaltyWidgetEnabled = dispensary?.storeSettings?.showLoyaltyRewards ?? false;

  // Show order success modals when viewOrderId is stored in session storage for embedded menus
  const isForceFullWidthCheckoutEnabled = flags['fintech.platform.force-full-width-checkout'] ?? false;
  // Where the Sponsored Brand carousel renders, resolved once here and threaded into
  // HomepageCarouselSections below. One resolution per page keeps the top mount and the in-lineup
  // mount mutually exclusive, so a single shopper never triggers two auctions or two impression
  // sets.
  const {
    renderAtTop: showHomePageSponsoredBrandCarousel,
    slot: sponsoredBrandSlot,
  } = useHomeSponsoredBrandPlacement();

  const RemoteHero = useRemoteComponent(RemoteComponentSlot.Hero);
  const RemoteLoyaltyWidget = useRemoteComponent<React.ComponentProps<typeof DutchieLoyaltyWidget>>(
    RemoteComponentSlot.LoyaltyWidget,
    { fallback: DutchieLoyaltyWidget }
  );
  const RemoteLoginWidget = useRemoteComponent<React.ComponentProps<typeof LoginCta>>(RemoteComponentSlot.LoginWidget, {
    fallback: LoginCta,
  });

  // don't show anonymous checkout for medical menus/orders ENG-52870
  const useAnonymousKioskCheckout =
    dispensary?.orderTypesConfigV2?.kiosk?.anonymousCheckout && Cart.menuType === `rec` && !isKioskProEnabled;

  const productsForHomePage = useProductsForHomePage({ dispensaryId });

  // A dispensary can now add a RECOMMENDED_FOR_YOU homepage section (PF-5771), which
  // HomepageCarouselSections renders below. Avoid rendering this same carousel twice:
  // only show it unconditionally here when no such section is configured, same as it
  // always has for every dispensary that hasn't opted into the configurable section.
  const { menuSections, loading: menuSectionsLoading } = useDispensaryMenuSections();
  const hasRecommendedForYouSection = menuSections.some(
    (section: { sectionType?: string }) => section.sectionType === 'RECOMMENDED_FOR_YOU'
  );
  const showLegacyPersonalizedProducts = !menuSectionsLoading && !hasRecommendedForYouSection;

  useEffect(() => {
    amplitude.log(AmplitudeEvents.menu.viewHomepage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bannerIsBlank = useMemo(
    () => !dispensary?.menuBannerHtml || isBlank(getHtmlPlainText(dispensary?.menuBannerHtml)),
    [dispensary?.menuBannerHtml]
  );

  const showMenuBanner =
    !bannerIsBlank && UI.isVariant([`store-front`, `embedded`, ...(useAnonymousKioskCheckout ? [`kiosk`] : [])]);

  // Effect to handle page load event
  useEffect(() => {
    function handleLoad(): void {
      setIsPageLoaded(true);
      window.removeEventListener('load', handleLoad);
    }

    setIsPageLoaded(document.readyState === 'complete');

    if (document.readyState !== 'complete') {
      window.addEventListener('load', handleLoad);
    }
  }, []);

  // Effect to handle modal opening after page load
  useEffect(() => {
    if (
      !isForceFullWidthCheckoutEnabled ||
      !UI.isEmbedded ||
      !dispensary?.embedSettings?.enableFullScreenEmbeddedCheckout ||
      !isPageLoaded
    ) {
      return;
    }

    const storedOrderId = getPersistedValue('viewOrderId');
    const stashedOrder = getPersistedValue('stashedOrder');

    if (storedOrderId) {
      if (!user.hasExternalUser && stashedOrder) {
        // For guest users, show guest checkout success modal with order data
        try {
          Cart.stashedOrder = JSON.parse(stashedOrder);
          UI.viewingGuestOrderSuccess = true;
        } catch (error) {
          // If JSON parsing fails, log error and continue without showing success modal
          console.error('Failed to parse stashed order:', error);
          UI.viewingGuestOrderSuccess = false;
        }
      } else {
        // For logged in users, show order status modal
        user.viewOrderId = storedOrderId;
        void openModal(ModalNames.orderStatus);
      }
    }
  }, [
    user,
    openModal,
    Cart,
    UI,
    dispensary?.embedSettings?.enableFullScreenEmbeddedCheckout,
    isForceFullWidthCheckoutEnabled,
    isPageLoaded,
  ]);

  if (needsHomePageLoadingState(dispensary)) {
    return <HomePageLoadingState />;
  }

  return (
    <MainContent>
      <div data-testid='storefront-hero'>
        <RemoteHero />
      </div>

      {!!dispensary?.imageBanners?.length && !UI.isMobileEcommApp && !showHomeIncentiveCarousel && (
        <MenuImageBanner banners={removeTypename(dispensary.imageBanners)} />
      )}
      {!UI.isMobileEcommApp && showHomeIncentiveCarousel && (
        <HomeImageBannerCarousel banners={removeTypename(dispensary?.imageBanners ?? [])} />
      )}

      {showMenuBanner && (
        <MenuBanner htmlContent={dispensary?.menuBannerHtml ?? undefined} color={dispensary?.menuBannerColor ?? ''} />
      )}

      {!hideCategoryTiles && (
        <div data-testid='homepage-categories'>
          <HomeCategoryTiles categories={categories} />
        </div>
      )}

      {isLoyaltyWidgetEnabled && (
        <RemoteLoyaltyWidget
          hasTiers={hasTiers}
          redemptionType={redemptionType ?? ''}
          isWidgetLink={!isKioskProEnabled}
          displayCTA
        />
      )}

      <RemoteLoginWidget margin='20px auto 40px' hideIfNeitherEnabled />

      {showHomePageSponsoredBrandCarousel && (
        <CarouselTitleSlotProvider carouselId='sponsored-brand'>
          <HomePageSponsoredBrandCarousel />
        </CarouselTitleSlotProvider>
      )}

      {showBuyItAgainCarousel && (
        <CarouselTitleSlotProvider carouselId='buy-it-again'>
          <BuyItAgainCarousel />
        </CarouselTitleSlotProvider>
      )}

      {(!UI.isKiosk || isKioskProEnabled) && showLegacyPersonalizedProducts && (
        <CarouselTitleSlotProvider carouselId='recommended-for-you'>
          <PersonalizedProducts {...productsForHomePage} />
        </CarouselTitleSlotProvider>
      )}

      <HomepageCarouselSections sponsoredBrandSlot={sponsoredBrandSlot} />
    </MainContent>
  );
}
